'use client';

import { useState, useRef, useEffect } from 'react';
import { Download, Image, FileText, Loader2, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { downloadPNG } from '@/lib/export/to-png';
import { downloadPDF } from '@/lib/export/to-pdf';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuth } from '@/components/auth/auth-provider';
import { useUpgrade } from '@/components/upgrade/UpgradeModal';
import { AuthModal } from '@/components/auth/auth-modal';
import { createClient } from '@/lib/supabase/client';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { createSavedTemplate, updateSavedTemplate } from '@/lib/saved-templates';
import { cn } from '@/lib/utils';

interface ExportButtonsProps {
  receiptRef: React.RefObject<HTMLDivElement | null>;
  templateName?: string;
  templateSlug?: string;
  position?: 'left' | 'right';
  // Compact variant: icon-only blue button (used in the mobile sub-header where space is tight).
  compact?: boolean;
}

export function ExportButtons({
  receiptRef,
  templateName = 'receipt',
  templateSlug,
  position = 'left',
  compact = false,
}: ExportButtonsProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState<'png' | 'pdf' | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { requireAuth, showAuthModal, setShowAuthModal } = useAuthGate();
  const { user, isPaid } = useAuth();
  const { open: openUpgrade } = useUpgrade();
  const template = useReceiptBuilder((s) => s.template);
  const savedTemplateId = useReceiptBuilder((s) => s.savedTemplateId);
  const markSaved = useReceiptBuilder((s) => s.markSaved);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const runExport = async (type: 'png' | 'pdf') => {

    if (!receiptRef.current) {
      console.error('Receipt ref is not available');
      alert('Unable to export: Receipt element not found. Please refresh the page.');
      return;
    }

    setIsExporting(true);
    setExportingType(type);
    setShowDropdown(false);
    
    try {
      const filename = `${templateName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
      
      // Find the receipt paper element
      const receiptPaper = receiptRef.current.querySelector('.receipt-paper') as HTMLElement;
      const elementToExport = receiptPaper || receiptRef.current;
      
      if (type === 'png') {
        await downloadPNG(elementToExport, {
          filename: `${filename}.png`,
          scale: 2,
        });
      } else {
        await downloadPDF(elementToExport, {
          filename: `${filename}.pdf`,
          scale: 2,
          pageSize: 'receipt',
        });
      }

      if (user) {
        try {
          const supabase = createClient();
          await supabase.from('receipt_downloads').insert({
            user_id: user.id,
            template_name: templateName,
            template_slug: templateSlug || null,
            export_type: type,
          });
        } catch { /* non-critical */ }

        // Auto-save the downloaded template to My Templates.
        // If we already track a saved row, update it; otherwise create a new one.
        try {
          if (savedTemplateId) {
            await updateSavedTemplate(savedTemplateId, { data: template });
            markSaved(savedTemplateId);
          } else {
            const created = await createSavedTemplate(templateName, template);
            markSaved(created.id);
          }
        } catch { /* non-critical — download already succeeded */ }
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the browser console for details.`);
    } finally {
      setIsExporting(false);
      setExportingType(null);
    }
  };

  const handleExport = (type: 'png' | 'pdf') => {
    if (!requireAuth(() => runExport(type))) return;
    runExport(type);
  };

  return (
    <>
    <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isExporting}
        aria-label={compact ? 'Download' : undefined}
        className={cn(
          'bg-[#0068D6] hover:bg-[#0066DD] text-white shadow-sm',
          compact ? 'h-8 w-8 p-0' : 'h-10 px-4 gap-2'
        )}
      >
        {isExporting ? (
          compact ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Exporting...</span>
            </>
          )
        ) : compact ? (
          <Download className="h-4 w-4" />
        ) : (
          <>
            <Download className="h-4 w-4" />
            <span className="text-sm">Download</span>
            <ChevronDown className="h-4 w-4" />
          </>
        )}
      </Button>

      {/* Dropdown Menu */}
      {showDropdown && !isExporting && (
        <div 
          className="absolute top-full mt-1 bg-white rounded-lg border shadow-lg overflow-hidden z-50"
          style={{ 
            minWidth: '160px',
            right: position === 'right' ? 0 : 'auto',
            left: position === 'left' ? 0 : 'auto',
          }}
        >
          <button
            onClick={() => handleExport('png')}
            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
          >
            <Image className="h-4 w-4 text-gray-600" />
            <span className="text-sm text-gray-900">Download PNG</span>
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left border-t"
          >
            <FileText className="h-4 w-4 text-gray-600" />
            <span className="text-sm text-gray-900">Download PDF</span>
          </button>
          {!isPaid && (
            <button
              onClick={() => {
                setShowDropdown(false);
                openUpgrade();
              }}
              className="w-full px-4 py-3 flex items-center gap-3 bg-[#F0F8FF] hover:bg-[#E7F5FF] transition-colors text-left border-t"
            >
              <Sparkles className="h-4 w-4 text-[#0068D6]" />
              <span className="text-sm font-medium text-[#0b4f92]">Remove watermark</span>
            </button>
          )}
        </div>
      )}
    </div>
    </>
  );
}
